import React from 'react';

const safeImageCache = new Map<string, string>();

/**
 * Universal Image & Cover URL Resolver for IPTV
 * Safely proxies HTTP image assets to prevent Mixed Content blocking in HTTPS environments,
 * optimizes oversized CDN resolutions (TMDB original -> w342), and caches results.
 */
export function getSafeImageUrl(url?: string, fallback = ''): string {
  if (!url) return fallback;
  let trimmed = url.trim();
  if (!trimmed) return fallback;

  // Cache hit
  const cached = safeImageCache.get(trimmed);
  if (cached) return cached;

  // Otimização TMDB: substitui resoluções gigantes ('original', 'w1280', 'w780') por 'w342' (140x mais leve e instantâneo)
  if (trimmed.includes('image.tmdb.org/t/p/')) {
    trimmed = trimmed
      .replace('/t/p/original/', '/t/p/w342/')
      .replace('/t/p/w1280/', '/t/p/w342/')
      .replace('/t/p/w780/', '/t/p/w342/');
  }

  let result = trimmed;

  // CDNs públicas seguras em HTTPS que possuem CORS liberado e CDN ultra rápida direta
  const isDirectSafeCdn =
    trimmed.includes('image.tmdb.org') ||
    trimmed.includes('themoviedb.org') ||
    trimmed.includes('images.unsplash.com') ||
    trimmed.includes('imgur.com') ||
    trimmed.includes('wikimedia.org') ||
    trimmed.includes('githubusercontent.com');

  if (isDirectSafeCdn && trimmed.startsWith('https://')) {
    result = trimmed;
  } else {
    // Em produção PHP na HostGator, usa /api/proxy.php apenas para imagens HTTP inseguras ou IPs locais
    const proxyPath = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('127.0.0.1')
      ? '/api/proxy.php'
      : '/api/proxy';

    // Se o site atual é HTTPS e a imagem é HTTP pura (Mixed Content de verdade), passa pelo
    // proxy. Se a página atual JÁ é HTTP (como no APK do TV Box/Stick, que carrega via
    // http://iptv.mariozeli.com.br), não existe risco de Mixed Content nenhum — carrega a
    // imagem direto, sem sobrecarregar o proxy à toa com toda capa/logo da grade de uma vez.
    const pageIsHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
    if (trimmed.startsWith('http://') && pageIsHttps) {
      result = `${proxyPath}?url=${encodeURIComponent(trimmed)}`;
    } else if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
      result = trimmed;
    } else {
      result = trimmed || fallback;
    }
  }

  if (safeImageCache.size > 2000) {
    safeImageCache.clear();
  }
  safeImageCache.set(url.trim(), result);
  return result;
}

/**
 * Generate a dynamic SVG Data-URI poster when a series or movie doesn't have an official cover.
 */
export function generateDynamicPoster(title: string, category = 'SÉRIE', year?: string | number): string {
  const safeTitle = (title || 'Série').slice(0, 30).replace(/[<>&"]/g, '');
  const safeCat = (category || 'VOD').slice(0, 20).replace(/[<>&"]/g, '').toUpperCase();
  const safeYear = year ? String(year) : '';

  // Deterministic hue from title
  let hash = 0;
  for (let i = 0; i < title.length; i++) {
    hash = title.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="300" height="450" viewBox="0 0 300 450">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="hsl(${hue}, 70%, 15%)" />
        <stop offset="50%" stop-color="hsl(${(hue + 40) % 360}, 60%, 10%)" />
        <stop offset="100%" stop-color="#09090b" />
      </linearGradient>
      <linearGradient id="badge" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stop-color="hsl(${hue}, 80%, 55%)" />
        <stop offset="100%" stop-color="hsl(${(hue + 40) % 360}, 80%, 55%)" />
      </linearGradient>
    </defs>
    <rect width="300" height="450" fill="url(#bg)" rx="16" />
    <circle cx="150" cy="180" r="45" fill="hsl(${hue}, 70%, 20%)" opacity="0.6" />
    <path d="M140 160 L170 180 L140 200 Z" fill="#ffffff" opacity="0.9" />
    <rect x="20" y="24" width="${Math.min(180, safeCat.length * 10 + 20)}" height="24" rx="6" fill="url(#badge)" />
    <text x="30" y="40" fill="#000000" font-size="11" font-weight="900" font-family="sans-serif">${safeCat}</text>
    <text x="150" y="270" fill="#ffffff" font-size="18" font-weight="800" font-family="sans-serif" text-anchor="middle">
      <tspan x="150" dy="0">${safeTitle}</tspan>
    </text>
    <text x="20" y="425" fill="#a1a1aa" font-size="12" font-weight="600" font-family="sans-serif">${safeYear}</text>
    <text x="280" y="425" fill="hsl(${hue}, 80%, 55%)" font-size="11" font-weight="800" font-family="sans-serif" text-anchor="end">NEXUS VOD</text>
  </svg>`;

  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * Safely handles <img> element error events to set a fail-safe SVG fallback without looping.
 */
export function handleImageError(e: React.SyntheticEvent<HTMLImageElement, Event>, title = 'IPTV') {
  const target = e.currentTarget;
  rememberFailedImage(target);
  target.onerror = null; // Prevent infinite error loops if fallback fails
  const safeTitle = title.slice(0, 15).replace(/[<>&"]/g, '');
  target.src = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128"><rect width="128" height="128" fill="#18181b" rx="12"/><text x="50%" y="50%" fill="#71717a" dominant-baseline="middle" text-anchor="middle" font-size="11" font-weight="700" font-family="sans-serif">${safeTitle}</text></svg>`
  )}`;
}

/**
 * Safe fallback for Channel Logos in Grid / Lists
 * Tries safe proxy first if not proxied yet; if it still fails, generates a vibrant channel badge with channel number & initials.
 */
export function handleChannelLogoError(
  e: React.SyntheticEvent<HTMLImageElement, Event>,
  name: string,
  num?: number | string
) {
  const target = e.currentTarget;
  const originalSrc = target.getAttribute('data-original-src') || target.src;
  
  // If we haven't tried proxying this specific URL yet, try through proxy.php
  if (!target.getAttribute('data-tried-proxy') && (originalSrc.startsWith('http://') || originalSrc.startsWith('https://'))) {
    target.setAttribute('data-tried-proxy', 'true');
    const proxyUrl = getSafeImageUrl(originalSrc);
    if (proxyUrl && proxyUrl !== target.src) {
      target.src = proxyUrl;
      return;
    }
  }

  rememberFailedImage(target, originalSrc);
  target.onerror = null; // Prevent loop
  const cleanName = (name || 'TV').replace(/\[.*?\]|\(.*?\)|FHD|HD|SD|4K/gi, '').trim().slice(0, 12);
  const channelNum = num ? `#${num}` : '';
  
  // Deterministic color from name
  let hash = 0;
  for (let i = 0; i < (name || '').length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;

  target.src = `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="160" height="90" viewBox="0 0 160 90"><defs><linearGradient id="g" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stop-color="hsl(${hue}, 60%, 20%)"/><stop offset="100%" stop-color="#09090b"/></linearGradient></defs><rect width="160" height="90" fill="url(#g)" rx="10"/><rect x="2" y="2" width="156" height="86" fill="none" stroke="hsl(${hue}, 70%, 40%)" stroke-width="1.5" rx="8"/><text x="14" y="24" fill="hsl(${hue}, 80%, 70%)" font-size="10" font-weight="900" font-family="sans-serif">${channelNum}</text><text x="50%" y="54%" fill="#ffffff" dominant-baseline="middle" text-anchor="middle" font-size="14" font-weight="900" font-family="sans-serif">${cleanName}</text></svg>`
  )}`;
}

/**
 * Fallback for Movie / Series poster error with personalized cover badge
 */
export function handleMovieImageError(
  e: React.SyntheticEvent<HTMLImageElement, Event>,
  title: string,
  category = 'FILME',
  year?: string | number
) {
  const target = e.currentTarget;
  rememberFailedImage(target);
  target.onerror = null;
  target.src = generateDynamicPoster(title, category, year);
}

/**
 * Guarda o endereço da imagem que falhou (antes de trocar pelo desenho provisório), para tentar de novo
 * quando a internet voltar — sem isto o endereço original se perdia.
 */
function rememberFailedImage(target: HTMLImageElement, original?: string) {
  try {
    const src = original || target.getAttribute('src') || '';
    if (src && !src.startsWith('data:') && !target.hasAttribute('data-failed-src')) {
      target.setAttribute('data-failed-src', src);
    }
  } catch {}
}

/** Internet voltou: tenta de novo as capas/logos que falharam. Retorna quantas foram retentadas. */
export function retryFailedImages(): number {
  if (typeof document === 'undefined') return 0;
  const imgs = Array.from(document.querySelectorAll<HTMLImageElement>('img[data-failed-src]'));
  imgs.forEach((img) => {
    const src = img.getAttribute('data-failed-src') || '';
    img.removeAttribute('data-failed-src');
    img.removeAttribute('data-tried-proxy');
    if (src) img.src = src;
  });
  return imgs.length;
}


